import { IGraph } from 'affiny-core/modules/api/models/compatibilityReport/compatibilityReport.types';
import { ECounters } from 'affiny-core/modules/api/models/counters/counters.types';
import { ECompatibilityReportType } from 'affiny-core/modules/api/models/member/member.types';
import { IMemberChoices } from 'affiny-core/modules/api/models/memberchoice/memberchoice.types';
import {
  EReflistChapterName,
  TReflistQuestion,
} from 'affiny-core/modules/api/repositories/reflists/reflists.types';
import { EAsyncEventTypes } from 'affiny-core/modules/asynchronousState/types';
import { ENotificationLabels } from 'affiny-core/modules/counters/types';
import { IPicture, PICTURE_FORMAT } from 'affiny-core/modules/pictures/types';
import { EReflistSubject } from 'affiny-core/modules/reflists/types';
import { IRootState } from 'affiny-core/modules/state';
import {
  selectAccountAttributeElement,
  selectAccountAttributes,
  selectAccountId,
  selectMainPicture,
  selectPictures,
  selectSecondaryPictures,
} from 'affiny-core/redux/selectors/accountSelectors';
import { selectIsLoading } from 'affiny-core/redux/selectors/asyncStateSelectors';
import {
  isDifferentValuesGraph,
  isDifferentVisionGraph,
  isHorizontalGraph,
  isOtherValuesGraph,
  isSimilarValuesGraph,
  isSimilarVisionGraph,
  selectFilteredGraphs,
} from 'affiny-core/redux/selectors/compatReportSelectors';
import { selectFeatureFlippingAttribute } from 'affiny-core/redux/selectors/configSelectors';
import {
  selectCounter,
  selectCounters,
  selectHasNotification,
} from 'affiny-core/redux/selectors/countersSelectors';
import {
  selectMemberChoiceByQuestions,
  selectMemberChoicesByAccountId,
  selectMyProfileMemberChoices,
  selectProfileDisplayVisitedTabData,
  selectProfileJobByMemberId,
} from 'affiny-core/redux/selectors/memberChoicesSelectors';
import {
  selectMember,
  selectMemberAttribute,
  selectMemberHasPrivatePicture,
  selectMemberProfileData,
} from 'affiny-core/redux/selectors/membersSelectors';
import {
  selectPersonalityGraphs,
  selectPersonalityIntroAnalysis,
  selectPersonalityType,
  selectValuesGraphs,
  selectVisionGraphs,
} from 'affiny-core/redux/selectors/persoReportSelectors';
import {
  selectPicturesByAccountId,
  selectPictureUriById,
} from 'affiny-core/redux/selectors/picturesSelectors';
import {
  getNextChapterLabel,
  getPreviousChapterLabel,
  hasNextChapter,
  hasNextQuestions,
  hasPreviousChapter,
  hasPreviousQuestions,
  selectNumberOfQuestionsForChapter,
  selectQuestions,
  selectQuestionsForKeys,
} from 'affiny-core/redux/selectors/reflistsSelectors';
import { selectCurrentSpecialOffer } from 'affiny-core/redux/selectors/specialOfferSelectors';
import { selectIsSub } from 'affiny-core/redux/selectors/subscriptionsSelectors';
import { useSelector } from 'react-redux';

// ME
export const useIsSub = () => useSelector(selectIsSub);

export const useSelectAccountId = () => useSelector(selectAccountId);

export const useSelectMyPictures = () => {
  let mainPicture = useSelector(selectMainPicture) as IPicture;
  let otherPictures = useSelector(selectSecondaryPictures);
  const allPictures = useSelector(selectPictures);

  if (!mainPicture) {
    // This is needed so that always one picture is shown in big
    mainPicture = otherPictures[0];
    otherPictures = otherPictures.slice(1);
  }

  return {
    mainPicture,
    otherPictures,
    allPictures,
  };
};

export const useSelectPersonalityType = () =>
  useSelector(selectPersonalityType);

export const useSelectPersonalityIntroAnalysis = () =>
  useSelector(selectPersonalityIntroAnalysis);

export const useSelectPersonalityReportGraphs = () =>
  useSelector((state: IRootState) => ({
    values: selectValuesGraphs(state),
    vision: selectVisionGraphs(state),
    personality: selectPersonalityGraphs(state),
  }));

export const useSelectAccountAttributes = () =>
  useSelector(selectAccountAttributes);

export const useAccountAttributeElement = (attribute: string) =>
  useSelector((state: IRootState) =>
    selectAccountAttributeElement(state, attribute)
  );

export const useSelectCurrentSpecialOffer = () =>
  useSelector(selectCurrentSpecialOffer);

export const useSelectCounters = (counterKey?: ECounters) =>
  useSelector((state: IRootState) =>
    counterKey ? selectCounter(state, counterKey) : selectCounters(state)
  );

export const useSelectMyProfileMemberChoices = () =>
  useSelector(selectMyProfileMemberChoices) as Partial<IMemberChoices>;

export const useSelectOtherProfileMemberChoices = (accountId: string) =>
  useSelector(
    (state: IRootState) =>
      selectMemberChoicesByAccountId(state, accountId) ?? {}
  );

export const useSelectMemberChoiceByQuestions = (
  questions: TReflistQuestion[]
) =>
  useSelector((state: IRootState) =>
    selectMemberChoiceByQuestions(state, questions)
  );

export const useSelectQuestions = (
  chapterLabel: string | EReflistChapterName,
  questionIndex?: string,
  offset?: number
) =>
  useSelector((state: IRootState) =>
    selectQuestions(
      state,
      chapterLabel as EReflistChapterName,
      Number(questionIndex) + (offset ? offset : 0)
    )
  );

export const useSelectQuestionsForKeys = (reflistKeys: string[]) =>
  useSelector((state: IRootState) =>
    selectQuestionsForKeys(state, reflistKeys)
  ) as TReflistQuestion[];

export const useHasNextQuestions = (
  chapterLabel: string | EReflistChapterName,
  questionIndex?: string
) =>
  useSelector((state: IRootState) =>
    hasNextQuestions(
      state,
      chapterLabel as EReflistChapterName,
      Number(questionIndex)
    )
  );

export const useHasNextChapter = (chapterLabel: string | EReflistChapterName) =>
  useSelector((state: IRootState) =>
    hasNextChapter(state, chapterLabel as EReflistChapterName)
  );

export const useGetNextChapterLabel = (
  chapterLabel: string | EReflistChapterName
) =>
  useSelector((state: IRootState) =>
    getNextChapterLabel(state, chapterLabel as EReflistChapterName)
  );

export const useHasPreviousChapter = (
  chapterLabel: string | EReflistChapterName
) =>
  useSelector((state: IRootState) =>
    hasPreviousChapter(state, chapterLabel as EReflistChapterName)
  );

export const useGetPreviousChapterLabel = (
  chapterLabel: string | EReflistChapterName
) =>
  useSelector((state: IRootState) =>
    getPreviousChapterLabel(state, chapterLabel as EReflistChapterName)
  );

export const useHasPreviousQuestions = (
  chapterLabel: string | EReflistChapterName,
  questionIndex?: string
) =>
  useSelector((state: IRootState) =>
    hasPreviousQuestions(
      state,
      chapterLabel as EReflistChapterName,
      Number(questionIndex)
    )
  );

export const useSelectNumberOfQuestionsForChapter = (
  chapterLabel: string | EReflistChapterName
) =>
  useSelector((state: IRootState) =>
    selectNumberOfQuestionsForChapter(
      state,
      chapterLabel as EReflistChapterName
    )
  ) || 0;

export const useIsLoading = (asyncEventType: EAsyncEventTypes) =>
  useSelector((state: IRootState) => selectIsLoading(state, asyncEventType));

export const useSelectHasNotification = (
  notificationLabels: ENotificationLabels
) =>
  useSelector((state: IRootState) =>
    selectHasNotification(state, notificationLabels)
  );

export const useSelectFeatureFlippingAttribute = (flippingKey: string) =>
  useSelector((state: IRootState) =>
    selectFeatureFlippingAttribute(state, flippingKey)
  );

// OTHER
export const useSelectMember = (accountId?: string) =>
  useSelector((state: IRootState) =>
    accountId ? selectMember()(state, accountId) : undefined
  );

export const useMemberAttribute = (attribute: string, accountId?: string) =>
  useSelector((state: IRootState) =>
    accountId ? selectMemberAttribute(state, accountId, attribute) : undefined
  );

export const useSelectMemberProfileData = (
  attribute: string,
  accountId?: string
) =>
  useSelector((state: IRootState) =>
    accountId ? selectMemberProfileData(state, accountId, attribute) : undefined
  );

export const useSelectProfileJobByMemberId = (
  accountId?: string,
  subject?: EReflistSubject
) =>
  useSelector((state: IRootState) =>
    accountId
      ? selectProfileJobByMemberId(state, accountId, subject)
      : undefined
  );

export const useSelectPicturesByAccountId = (accountId: string) =>
  useSelector((state: IRootState) =>
    selectPicturesByAccountId(state, accountId)
  );

export const useSelectPictureUriById = (
  pictureId: string,
  format?: PICTURE_FORMAT,
  isBlurredFormat?: boolean
) =>
  useSelector((state: IRootState) =>
    pictureId
      ? selectPictureUriById(
          state,
          pictureId,
          format ? format : PICTURE_FORMAT.SMALL,
          isBlurredFormat ? isBlurredFormat : false
        )
      : undefined
  );

export const useHasPrivatePictures = (accountId?: string) =>
  useSelector((state: IRootState) =>
    accountId ? selectMemberHasPrivatePicture(state, accountId) : undefined
  );

export const useSelectProfileDisplayVisitedTabData = (
  accountId?: string,
  subject?: EReflistSubject
) =>
  useSelector((state: IRootState) =>
    accountId
      ? selectProfileDisplayVisitedTabData(state, accountId, subject)
      : {}
  );

export const useSelectCompatibilityReportGraphs = (
  reportType: ECompatibilityReportType,
  accountId: string,
  graphType?: string
) => {
  let filterMethod: (graph: IGraph) => boolean = () => true;

  if (graphType) {
    switch (
      true // switch on reportType && graphType
    ) {
      case reportType === ECompatibilityReportType.VALUE &&
        graphType === 'different':
        filterMethod = isDifferentValuesGraph;
        break;
      case reportType === ECompatibilityReportType.VALUE &&
        graphType === 'similar':
        filterMethod = isSimilarValuesGraph;
        break;
      case reportType === ECompatibilityReportType.VALUE &&
        graphType === 'other':
        filterMethod = isOtherValuesGraph;
        break;
      case reportType === ECompatibilityReportType.VISION &&
        graphType === 'different':
        filterMethod = isDifferentVisionGraph;
        break;
      case reportType === ECompatibilityReportType.VISION &&
        graphType === 'similar':
        filterMethod = isSimilarVisionGraph;
        break;
      case reportType === ECompatibilityReportType.VISION &&
        graphType === 'horizontal':
        filterMethod = isHorizontalGraph;
        break;
    }
  }

  return useSelector(selectFilteredGraphs(reportType, filterMethod)(accountId));
};
